import React, { useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Box, Typography, MenuItem, Select as MuiSelect, FormHelperText, InputAdornment } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

import { toast } from 'components/global/CUK/Toast/Toast'
import { DatePicker, DatePickerAnchorField } from 'components/global/CUK/DatePicker'
import {
  TIMEZONES,
  getDefaultTimezone,
  getTimezoneByOffset,
  getTimezoneOffset,
  formatTimezoneDisplay
} from 'utils/timezones'

import useSave, { buildSchema } from '../use-actions'
import { useDetail } from '../use-event-ssids'
import { trackEvent } from 'analytics'

import Modal from './Modal'
import {
  StyledTextField,
  StyledTextArea,
  ButtonContainer,
  CancelButton,
  SubmitButton
} from './EventSsidFormStyles'

const generatePassphrase = () => {
  const length = 16
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_-.;'
  let password = ''
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length))
  }
  return password
}

const ActionButtons = ({
  onConfirm,
  onCancel,
  loading = false,
  submitLabel = 'ui_elements.create'
}) => {
  const { t } = useTranslation()

  return (
    <ButtonContainer>
      <CancelButton onClick={onCancel}>
        <Typography sx={{ fontSize: '12px', color: '#333' }}>
          {t('ui_elements.cancel')}
        </Typography>
      </CancelButton>
      <SubmitButton
        onClick={onConfirm}
        disabled={loading}
        loading={loading}
      >
        <Typography sx={{ fontSize: '12px' }}>
          {loading ? t('ui_elements.in_progress') : t(submitLabel)}
        </Typography>
      </SubmitButton>
    </ButtonContainer>
  )
}

interface EventSsidFormContentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item?: any
  onClose?: () => void
  fromCard?: boolean
}

const EventSsidFormContent: React.FC<EventSsidFormContentProps> = ({ item, onClose, fromCard = false }) => {
  const { t } = useTranslation()
  const [save, { loading }] = useSave()
  const navigate = useNavigate()
  const [showPassphrase, setShowPassphrase] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const defaultTimezone = useMemo(() => getDefaultTimezone(), [])

  const today = useMemo(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  }, [])

  const isEditMode = Boolean(item?.id)

  const defaultValues = useMemo(() => ({
    label: item?.label || '',
    name: item?.name || '',
    passphrase: item?.passphrase || '',
    description: item?.description || '',
    start_date: item?.start_date ? new Date(item.start_date) : null,
    start_time: item?.start_time ? item.start_time.substring(0, 5) : '00:00',
    end_date: item?.end_date ? new Date(item.end_date) : null,
    end_time: item?.end_time ? item.end_time.substring(0, 5) : '23:59',
    utc_offset: item?.utc_offset
      ? getTimezoneByOffset(item.utc_offset, item.start_date, item.start_time)?.label || item.utc_offset
      : defaultTimezone.label
  }), [item, defaultTimezone])

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(buildSchema({ t })),
    mode: 'onBlur'
  })
  const startDate = methods.watch('start_date')
  const startTime = methods.watch('start_time')

  const getErrorMessage = (error: string) => {
    const errorMap = {
      'SsidLimitExceeded': t('event_ssids.errors.limit_exceeded'),
      'SsidNameNotUnique': t('event_ssids.errors.name_not_unique'),
      'SsidStartMustNotChange': t('event_ssids.errors.ssid_start_must_not_change'),
      'WasabiScheduledBadParams': t('event_ssids.errors.missing_fields'),
      'invalid format': t('event_ssids.errors.passphrase_invalid')
    }

    return errorMap[error] || t('event_ssids.save.error')
  }

  const handleSubmit = async (values) => {
    const timezone = TIMEZONES.find(tz => tz.label === values.utc_offset)
    const submitData = {
      label: values.label,
      name: values.name,
      passphrase: values.passphrase,
      description: values.description?.trim() || null,
      start_date: values.start_date,
      start_time: values.start_time.includes(':') && values.start_time.length === 5 
        ? `${values.start_time}:00` 
        : values.start_time,
      end_date: values.end_date,
      end_time: values.end_time.includes(':') && values.end_time.length === 5 
        ? `${values.end_time}:00` 
        : values.end_time,
      utc_offset: timezone
        ? getTimezoneOffset(timezone, values.start_date, values.start_time)
        : values.utc_offset,
      ...(item?.id && { id: item.id })
    }

    const { error } = await save(submitData)
    if (error) {
      toast.error(getErrorMessage(error))
    } else {
      toast.success(t('event_ssids.save.success'))
      trackEvent(isEditMode ? 'wifi_management_updated' : 'wifi_management_created', {
        has_description: Boolean(values.description?.trim())
      })
      if (fromCard && onClose) {
        onClose()
      } else {
        navigate('..')
      }
    }
  }

  const handleCancel = () => {
    if (fromCard && onClose) {
      onClose()
    } else {
      navigate('..')
    }
  }

  const handleGeneratePassphrase = () => {
    const newPassphrase = generatePassphrase()
    methods.setValue('passphrase', newPassphrase)
    setShowPassphrase(true)
    trackEvent('wifi_management_passphrase_generated')
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FormProvider {...methods}>
        <Stack sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box
            ref={scrollContainerRef}
            sx={{
              px: 3,
              pb: 3,
              overflowY: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <Stack gap={2}>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                  {t('event_ssids.form.event_label')} *
                </Typography>
                <Controller
                  control={methods.control}
                  name="label"
                  render={({ field, fieldState }) => (
                    <StyledTextField
                      {...field}
                      placeholder={t('event_ssids.form.event_label_placeholder')}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      hasError={Boolean(fieldState.error)}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                  {t('event_ssids.form.ssid')} *
                </Typography>
                <Controller
                  control={methods.control}
                  name="name"
                  render={({ field, fieldState }) => (
                    <StyledTextField
                      {...field}
                      placeholder={t('event_ssids.form.ssid_placeholder')}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      hasError={Boolean(fieldState.error)}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                  {t('event_ssids.form.passphrase')} *
                </Typography>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      control={methods.control}
                      name="passphrase"
                      render={({ field, fieldState }) => (
                        <StyledTextField
                          {...field}
                          type={showPassphrase ? 'text' : 'password'}
                          placeholder="*********"
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          hasError={Boolean(fieldState.error)}
                          InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box
                                onClick={() => setShowPassphrase(!showPassphrase)}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: '#656565'
                                }}
                              >
                                <i 
                                  className={`fa-solid fa-${showPassphrase ? 'eye-slash' : 'eye'}`} 
                                  style={{ fontSize: 12 }} 
                                />
                              </Box>
                            </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    onClick={handleGeneratePassphrase}
                    sx={{
                      minWidth: '72px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #EFEFEF',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: '#F7F7F7'
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#656565' }}>
                      {t('event_ssids.form.generate')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333' }}>
                    {t('event_ssids.form.description')}
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 400, color: '#989898' }}>
                    ({t('event_ssids.form.optional')})
                  </Typography>
                </Stack>
                <Controller
                  control={methods.control}
                  name="description"
                  render={({ field, fieldState }) => (
                    <StyledTextArea
                      {...field}
                      multiline
                      rows={4}
                      placeholder={t('event_ssids.form.description_placeholder')}
                      hasError={Boolean(fieldState.error)}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                  {t('event_ssids.form.dates')} *
                </Typography>
                <Controller
                  control={methods.control}
                  name="start_date"
                  render={({ field: { value }, fieldState }) => (
                    <Box>
                      <DatePicker
                        placement='left'
                        name="start_date"
                        defaultStartDate={value}
                        defaultEndDate={methods.watch('end_date')}
                        minDate={today}
                        blockPastDates={!item}
                        editMode={isEditMode}
                        field={
                          <DatePickerAnchorField
                            isdate={true}
                            locale="fr"
                            startDate={value}
                            endDate={methods.watch('end_date')}
                            placeholder={t('event_ssids.form.dates_placeholder')}
                            onReset={() => {
                              if (!isEditMode) {
                                methods.setValue('start_date', null)
                                methods.setValue('end_date', null)
                              } else {
                                const originalStart = item?.start_date ? new Date(item.start_date) : null
                                const originalEnd = item?.end_date ? new Date(item.end_date) : null
                                methods.setValue('start_date', originalStart)
                                methods.setValue('end_date', originalEnd)
                              }
                            }}
                            inputSize="large"
                            hasError={Boolean(fieldState.error)}
                            sx={{
                              width: '100%'
                            }}
                          />
                        }
                        onSubmit={(start, end) => {
                          methods.setValue('start_date', start)
                          methods.setValue('end_date', end)
                        }}
                      />
                      {fieldState.error && (
                        <FormHelperText error>
                          {fieldState.error.message}
                        </FormHelperText>
                      )}
                    </Box>
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                  {t('event_ssids.form.times')} *
                </Typography>
                <Stack direction="row" gap={1.5}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 9, color: '#656565', mb: 0.5 }}>
                      {t('event_ssids.form.start_time')}
                    </Typography>
                    <Controller
                      control={methods.control}
                      name="start_time"
                      render={({ field: { value, onChange }, fieldState }) => (
                        <Box>
                          <StyledTextField
                            type="time"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            error={Boolean(fieldState.error)}
                            hasError={Boolean(fieldState.error)}
                            inputProps={{
                              step: 60
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                                  cursor: 'pointer'
                                },
                                '& input[type="time"]': {
                                  fontSize: '12px'
                                }
                              }
                            }}
                          />
                          {fieldState.error && (
                            <FormHelperText error sx={{ fontSize: '10px', ml: 0, mt: 0.5 }}>
                              {fieldState.error.message}
                            </FormHelperText>
                          )}
                        </Box>
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 9, color: '#656565', mb: 0.5 }}>
                      {t('event_ssids.form.end_time')}
                    </Typography>
                    <Controller
                      control={methods.control}
                      name="end_time"
                      render={({ field: { value, onChange }, fieldState }) => (
                        <Box>
                          <StyledTextField
                            type="time"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            error={Boolean(fieldState.error)}
                            hasError={Boolean(fieldState.error)}
                            inputProps={{
                              step: 60
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                                  cursor: 'pointer'
                                },
                                '& input[type="time"]': {
                                  fontSize: '12px'
                                }
                              }
                            }}
                          />
                          {fieldState.error && (
                            <FormHelperText error sx={{ fontSize: '10px', ml: 0, mt: 0.5 }}>
                              {fieldState.error.message}
                            </FormHelperText>
                          )}
                        </Box>
                      )}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                  {t('event_ssids.form.timezone')} *
                </Typography>
                <Controller
                  control={methods.control}
                  name="utc_offset"
                  render={({ field: { value, onChange }, fieldState }) => (
                    <MuiSelect
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      error={Boolean(fieldState.error)}
                      displayEmpty
                      size="small"
                      sx={{
                        height: '40px',
                        borderRadius: '8px',
                        fontSize: 12,
                        width: '100%',
                        backgroundColor: 'white',
                        '& .MuiSelect-select': {
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '12px',
                          height: '40px'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldState.error ? 'error.main' : '#EFEFEF',
                          borderRadius: '8px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldState.error ? 'error.main' : '#EFEFEF'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldState.error ? 'error.main' : '#2570EB'
                        }
                      }}
                      IconComponent={(props) => (
                        <i
                          {...props}
                          className="fa-regular fa-chevron-down"
                          style={{
                            fontSize: 12,
                            color: '#464646',
                            marginRight: 12,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      startAdornment={
                        <i 
                          className="fa-regular fa-globe" 
                          style={{ 
                            fontSize: 12, 
                            color: '#656565', 
                            marginRight: 8 
                          }} 
                        />
                      }
                    >
                      {TIMEZONES.map(tz => (
                        <MenuItem key={tz.label} value={tz.label} sx={{ fontSize: 12 }}>
                          {formatTimezoneDisplay(
                            tz,
                            t,
                            startDate,
                            startTime
                          )}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  )}
                />
              </Box>
            </Stack>
          </Box>

          <ActionButtons
            loading={loading}
            submitLabel={item ? 'ui_elements.update' : 'ui_elements.create'}
            onConfirm={methods.handleSubmit(handleSubmit)}
            onCancel={handleCancel}
          />
        </Stack>
      </FormProvider>
    </LocalizationProvider>
  )
}

interface EventSsidFormProps {
  onClose?: () => void
  fromCard?: boolean
}

const EventSsidForm: React.FC<EventSsidFormProps> = ({ onClose, fromCard = false }) => {
  const { item, loading } = useDetail()

  if (loading) return null

  return (
    <Modal item={item} onClose={onClose}>
      <EventSsidFormContent item={item} onClose={onClose} fromCard={fromCard} />
    </Modal>
  )
}

export default EventSsidForm